/**
 * Mobile tour slideshow with illustrations
 */

import { useOnboarding } from "./use-onboarding";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, TrendingUp, BarChart3, LineChart, Wallet, Navigation, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { MOBILE_TOUR_STEPS, type MobileTourStep } from "./tour-steps";

// Illustration components for each mobile step
const StepIllustration = ({ type }: { type: MobileTourStep["illustration"] }) => {
  const baseClasses = "relative w-full h-48 rounded-xl border overflow-hidden flex items-center justify-center";
  const gradientBg = "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5";
  const borderColor = "border-emerald-500/30";

  switch (type) {
    case "welcome":
      return (
        <div className={cn(baseClasses, gradientBg, borderColor)}>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,transparent_49%,hsl(var(--border))_50%,transparent_51%,transparent_100%),linear-gradient(to_bottom,transparent_0%,transparent_49%,hsl(var(--border))_50%,transparent_51%,transparent_100%)] bg-[size:40px_40px] opacity-20" />
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="size-8 text-emerald-400" />
            </div>
            <span className="text-2xl font-bold text-emerald-400">NORDEX</span>
          </div>
        </div>
      );

    case "markets":
      return (
        <div className={cn(baseClasses, "bg-muted/30 border-border")}>
          <div className="w-full px-4">
            {/* Mock market selector */}
            <div className="bg-background rounded-lg border p-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-orange-400">₿</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">BTC-USD</div>
                  <div className="text-xs text-muted-foreground">Bitcoin</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-emerald-400">$95,420</div>
                  <div className="text-xs text-emerald-400">+2.4%</div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <BarChart3 className="size-6 text-emerald-400 animate-pulse" />
              <span className="text-xs text-muted-foreground ml-2">Tap to select markets</span>
            </div>
          </div>
        </div>
      );

    case "trade":
      return (
        <div className={cn(baseClasses, "bg-muted/30 border-border")}>
          <div className="w-full px-4">
            {/* Mock chart area */}
            <div className="bg-background rounded-lg border p-3 mb-3 h-20 flex items-center justify-center">
              <LineChart className="size-10 text-emerald-400/50" />
            </div>
            {/* Mock buy/sell buttons */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-emerald-500/20 rounded-lg py-2 text-center">
                <span className="text-xs font-medium text-emerald-400">BUY / LONG</span>
              </div>
              <div className="bg-red-500/20 rounded-lg py-2 text-center">
                <span className="text-xs font-medium text-red-400">SELL / SHORT</span>
              </div>
            </div>
          </div>
        </div>
      );

    case "portfolio":
      return (
        <div className={cn(baseClasses, "bg-muted/30 border-border")}>
          <div className="w-full px-4">
            <div className="flex items-center justify-center mb-3">
              <Wallet className="size-8 text-emerald-400" />
            </div>
            <div className="space-y-2">
              <div className="bg-background rounded-lg border p-2 flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Balance</span>
                <span className="text-sm font-medium">$10,000.00</span>
              </div>
              <div className="bg-background rounded-lg border p-2 flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Unrealized P&L</span>
                <span className="text-sm font-medium text-emerald-400">+$245.50</span>
              </div>
            </div>
          </div>
        </div>
      );

    case "navigation":
      return (
        <div className={cn(baseClasses, "bg-muted/30 border-border")}>
          <div className="absolute bottom-0 left-0 right-0">
            {/* Mock bottom nav */}
            <div className="bg-background border-t flex justify-around py-3">
              <div className="flex flex-col items-center gap-1">
                <BarChart3 className="size-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Markets</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <LineChart className="size-5 text-emerald-400" />
                <span className="text-[10px] text-emerald-400">Trade</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Wallet className="size-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Account</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pb-16">
            <Navigation className="size-6 text-emerald-400" />
            <span className="text-sm text-muted-foreground">Quick navigation</span>
          </div>
        </div>
      );

    case "complete":
      return (
        <div className={cn(baseClasses, gradientBg, borderColor)}>
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="size-8 text-emerald-400" />
            </div>
            <span className="text-lg font-semibold text-foreground">Ready to Trade!</span>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export const TourMobile = () => {
  const {
    isTouring,
    currentStep,
    totalSteps,
    isMobile,
    nextStep,
    prevStep,
    skipTour,
  } = useOnboarding();

  // Only render on mobile
  if (!isMobile || !isTouring) return null;

  const step = MOBILE_TOUR_STEPS[currentStep];
  if (!step) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={cn(
          "relative w-full max-w-md",
          "bg-card border border-border rounded-2xl shadow-2xl",
          "animate-in fade-in-0 zoom-in-95 duration-300"
        )}
      >
        {/* Green accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-emerald-500 to-transparent rounded-t-2xl" />

        {/* Skip button */}
        <button
          onClick={skipTour}
          className="absolute top-3 right-3 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors z-10"
          aria-label="Skip tour"
        >
          <X className="size-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Illustration */}
          <div className="mb-5">
            <StepIllustration type={step.illustration} />
          </div>

          {/* Text content */}
          <h3 className="text-xl font-semibold text-foreground mb-2 text-center">
            {step.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed text-center mb-6">
            {step.description}
          </p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-5">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  index === currentStep
                    ? "w-6 bg-emerald-500"
                    : "w-2 bg-muted-foreground/30"
                )}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={isFirstStep}
              className="flex-1 h-12"
            >
              <ChevronLeft className="size-4 mr-1" />
              Back
            </Button>
            <Button
              onClick={nextStep}
              className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isLastStep ? "Get Started" : "Next"}
              {!isLastStep && <ChevronRight className="size-4 ml-1" />}
            </Button>
          </div>

          {/* Skip link */}
          <button
            onClick={skipTour}
            className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
        </div>
      </div>
    </div>
  );
};

